<script lang="ts" setup>
import type { FieldProps } from '@/types/shared.types'
import FieldErrorMessage from '@/components/shared/Fields/FieldErrorMessage.vue'
import FieldHelpText from '@/components/shared/Fields/FieldHelpText.vue'
import FieldLabel from '@/components/shared/Fields/FieldLabel.vue'
import Icon from '@/components/shared/Icon.vue'

import { generateIconColor, handleNumberValidation, handleStringValidation } from '@/lib'
import { vMaska } from 'maska'
import { useField } from 'vee-validate'
import { computed, ref, onMounted, nextTick, watch } from 'vue'
import { tv } from 'tailwind-variants'

interface FieldTextProps extends FieldProps {
  autoHeight?: boolean
  code?: boolean
  mask?: string
  placeholder?: string
  prefixIcon?: string
  prefixText?: string
  rows?: number
  showClear?: boolean
  suffixIcon?: string
  suffixText?: string
  type: string
  fontWeight?: 'normal' | 'medium'
  characterClasses?: number
  initialFocus?: boolean
  // When true, stop key event propagation to parent containers (e.g., menus)
  stopKeyPropagation?: boolean
  variant?: 'default' | 'gray'
}

const props = withDefaults(defineProps<FieldTextProps>(), {
  rows: 4,
  showClear: false,
  showLabel: true,
  validate: true,
  code: false,
  fontWeight: 'normal',
  disabled: false,
  initialFocus: false,
  stopKeyPropagation: true,
  variant: 'default',
})

const emits = defineEmits<{
  clear: []
  click: [event: MouseEvent]
  keyup: [event: KeyboardEvent]
}>()

const showPassword = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)

const isTextArea = computed(() => props.type === 'textarea')
const isPassword = computed(() => props.type === 'password')

const shouldValidate = computed(() => {
  // Check if validation is explicitly enabled
  if (props.validate) return true
  // Check if there are any validation rules defined
  if (props.rules) {
    const validationKeys = ['max', 'min', 'regex'] as const
    const hasValidationRule = validationKeys.some((key) => props.rules?.[key] != null)
    if (hasValidationRule) return true
  }

  // Check if character class validation is required
  if (props.characterClasses && props.characterClasses > 0) return true

  return false
})

const { value, errorMessage, setValue, handleBlur } = useField<string>(
  () => props.name,
  (value) => {
    if (shouldValidate.value) {
      const isNumber = props.type === 'number'
      return isNumber
        ? handleNumberValidation(value, props.rules)
        : handleStringValidation(value, {
            ...props.rules,
            characterClasses: props.characterClasses,
            required: props.validate,
          })
    }
    return true
  },
  {
    validateOnValueUpdate: false,
  },
)

function handleClear() {
  setValue('')
  emits('clear')
}

function handleKeydown(event: KeyboardEvent) {
  if (!props.stopKeyPropagation) return
  // Allow Escape to bubble to close dialogs/menus and Tab to manage focus
  if (event.key === 'Escape' || event.key === 'Tab') return
  event.stopPropagation()
}

function handleKeyup(event: KeyboardEvent) {
  if (props.stopKeyPropagation && event.key !== 'Escape' && event.key !== 'Tab') {
    event.stopPropagation()
  }
  emits('keyup', event)
}

// Auto-resize textarea functionality
function adjustTextareaHeight() {
  if (!textareaRef.value || !props.autoHeight) return

  const textarea = textareaRef.value
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

watch(
  () => value.value,
  () => {
    if (isTextArea.value && props.autoHeight) {
      nextTick(adjustTextareaHeight)
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (props.initialFocus) {
    if (isTextArea.value) {
      textareaRef.value?.focus()
    } else {
      inputRef.value?.focus()
    }
  }
  if (isTextArea.value && props.autoHeight) {
    adjustTextareaHeight()
  }
})

const prefixTextRef = ref<HTMLSpanElement | null>(null)
const suffixTextRef = ref<HTMLSpanElement | null>(null)

const ICON_WIDTH = 20 // Icon width
const ICON_PADDING = 12 // Padding around icons
const BASE_PADDING = 12 // Base padding when no affixes

const inputVariants = tv({
  base: 'input-base',
  variants: {
    tone: {
      default: 'input-light-dark',
      gray: 'input-gray',
      error: 'input-error',
    },
    disabled: {
      true: 'input-disabled',
      false: 'input-enabled',
    },
    element: {
      input: 'input',
      textarea: 'textarea',
    },
  },
  defaultVariants: {
    tone: 'default',
    disabled: false,
    element: 'input',
  },
})

const resolvedTone = computed<'default' | 'gray' | 'error'>(() =>
  errorMessage.value ? 'error' : props.variant,
)

const inputClass = computed(() =>
  inputVariants({
    tone: resolvedTone.value,
    disabled: props.disabled,
    element: 'input',
  }),
)

const textareaClass = computed(() =>
  inputVariants({
    tone: resolvedTone.value,
    disabled: props.disabled,
    element: 'textarea',
  }),
)

// Compute left padding based on prefix elements
const computeLeftPadding = computed(() => {
  if (!props.prefixIcon && !props.prefixText) return `${BASE_PADDING}px`

  let padding = BASE_PADDING

  if (props.prefixIcon && props.prefixText && prefixTextRef.value) {
    // BASE_PADDING + ICON_WIDTH + ICON_PADDING + prefixTextWidth + ICON_PADDING
    padding =
      BASE_PADDING + ICON_WIDTH + ICON_PADDING + prefixTextRef.value.offsetWidth + ICON_PADDING
  } else if (props.prefixIcon) {
    padding += ICON_WIDTH + ICON_PADDING
  } else if (props.prefixText && prefixTextRef.value) {
    // BASE_PADDING + prefixTextWidth + ICON_PADDING
    padding = BASE_PADDING + prefixTextRef.value.offsetWidth + ICON_PADDING
  }

  return `${padding}px`
})

// We also need to compute the padding left of the prefixTextRef
const computeLeftTextPadding = computed(() => {
  if (!props.prefixIcon && !props.prefixText) return `${BASE_PADDING}px`

  let padding = BASE_PADDING

  if (props.prefixIcon) {
    padding += ICON_WIDTH + ICON_PADDING
  }

  return `${padding}px`
})

// Compute right padding based on suffix elements
const computeRightPadding = computed(() => {
  if (!props.suffixIcon && !props.suffixText) return `${BASE_PADDING}px`

  let padding = BASE_PADDING

  if (props.suffixIcon && props.suffixText && suffixTextRef.value) {
    // BASE_PADDING + ICON_WIDTH + ICON_PADDING + suffixTextWidth + ICON_PADDING
    padding =
      BASE_PADDING + ICON_WIDTH + ICON_PADDING + suffixTextRef.value.offsetWidth + ICON_PADDING
  } else if (props.suffixIcon) {
    padding += ICON_WIDTH + ICON_PADDING
  } else if (props.suffixText && suffixTextRef.value) {
    padding += suffixTextRef.value.offsetWidth
  }

  return `${padding}px`
})

const computeRightTextPadding = computed(() => {
  if (!props.suffixIcon && !props.suffixText && !isPassword.value && !props.showClear)
    return `${BASE_PADDING}px`

  let padding = BASE_PADDING

  if (props.suffixIcon || isPassword.value || props.showClear) {
    padding += ICON_WIDTH + ICON_PADDING
  }

  return `${padding}px`
})
</script>

<template>
  <div class="relative space-y-2 leading-none group">
    <FieldLabel v-if="label && showLabel" :name="name" :required="validate">
      {{ label }}
    </FieldLabel>

    <div class="relative leading-none">
      <Icon
        v-if="prefixIcon"
        :variant="prefixIcon"
        class="absolute left-3 top-1/2 w-5 h-5 transform -translate-y-1/2"
        :class="{ 'text-gray-400 cursor-not-allowed': disabled }"
        v-bind="generateIconColor(color)"
      />

      <span
        ref="prefixTextRef"
        class="absolute top-1/2 text-sm text-gray-500 transform -translate-y-1/2"
        :style="{ left: computeLeftTextPadding }"
        :class="{
          'text-gray-400 cursor-not-allowed select-none': disabled,
        }"
      >
        {{ prefixText }}
      </span>

      <input
        v-if="!isTextArea"
        ref="inputRef"
        :type="isPassword && showPassword ? 'text' : type"
        v-model="value"
        :class="inputClass"
        :style="[
          {
            paddingLeft: computeLeftPadding,
            paddingRight: computeRightPadding,
          },
        ]"
        v-maska
        :data-maska="mask ? mask : undefined"
        :disabled="disabled"
        :placeholder="placeholder"
        :name="name"
        :autocomplete="
          type === 'password' ? 'current-password' : type === 'email' ? 'email' : 'off'
        "
        @blur="(e) => handleBlur(e, true)"
        @click="(e) => emits('click', e)"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
      />

      <textarea
        v-if="isTextArea"
        ref="textareaRef"
        v-model="value"
        :class="textareaClass"
        :style="[
          {
            paddingLeft: computeLeftPadding,
            paddingRight: computeRightPadding,
          },
        ]"
        :disabled="disabled"
        :placeholder="placeholder"
        :name="name"
        :rows="rows"
        @blur="(e) => handleBlur(e, true)"
        @click="(e) => emits('click', e)"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
      />

      <span
        ref="suffixTextRef"
        class="absolute top-1/2 text-sm text-gray-500 transform -translate-y-1/2"
        :style="{ right: computeRightTextPadding }"
        :class="{
          'text-gray-400 cursor-not-allowed select-none': disabled,
        }"
      >
        {{ suffixText }}
      </span>

      <button
        v-if="isPassword"
        type="button"
        class="absolute right-3 top-1/2 rounded-md transform -translate-y-1/2 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:cursor-not-allowed"
        :aria-label="showPassword ? 'Hide password' : 'Show password'"
        :aria-pressed="showPassword"
        :disabled="disabled"
        tabindex="-1"
        @click="showPassword = !showPassword"
      >
        <Icon :variant="showPassword ? 'eye' : 'eye-off'" class="w-5 h-5 text-gray-500" />
      </button>
      <button
        v-if="showClear && value"
        type="button"
        class="flex absolute right-3 top-1/2 justify-center items-center w-5 h-5 text-gray-400 rounded transform -translate-y-1/2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-300"
        aria-label="Clear input"
        @click="handleClear"
      >
        <Icon variant="x" class="size-5" />
      </button>
      <Icon
        v-else-if="suffixIcon"
        :variant="suffixIcon"
        class="absolute right-3 top-1/2 w-5 h-5 transform -translate-y-1/2"
        :class="{ 'text-gray-400 cursor-not-allowed': disabled }"
        v-bind="generateIconColor(color)"
      />
    </div>
    <div v-if="help || errorMessage">
      <FieldHelpText v-if="help" :help-text="help" />
      <FieldErrorMessage v-if="errorMessage" :error-message="errorMessage" />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.input-base {
  @apply w-full text-sm font-normal rounded-md border-0 ring-1 ring-inset shadow-sm focus:ring-2 focus:outline-none;
}

.input {
  @apply h-9;
}

.textarea {
  @apply py-2 min-h-[2.25rem];
}

.input-light-dark {
  @apply bg-white text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-primary-300
         dark:text-gray-100 dark:bg-gray-800 dark:ring-gray-600 dark:placeholder-gray-500 dark:focus:ring-primary-400;

  &:disabled {
    @apply bg-gray-100 text-gray-400 placeholder:text-gray-400
           dark:bg-gray-700 dark:text-gray-500 dark:placeholder:text-gray-600;
  }
}

.input-gray {
  @apply bg-gray-100 text-gray-700 ring-gray-200 placeholder:text-gray-500 focus:ring-gray-300
         dark:bg-gray-800/80 dark:text-gray-200 dark:ring-gray-700 dark:placeholder:text-gray-500 dark:focus:ring-gray-600;

  &:disabled {
    @apply bg-gray-100/80 text-gray-500 placeholder:text-gray-500
           dark:bg-gray-700 dark:text-gray-500 dark:placeholder:text-gray-600;
  }
}

.input-error {
  @apply text-error-600 ring-error-300 bg-error-50 placeholder:text-error-500
         dark:text-error-300 dark:ring-error-500/50 dark:bg-error-800/50 dark:placeholder-error-400;

  &:disabled {
    @apply opacity-75;
  }
}

.input-disabled {
  @apply cursor-not-allowed select-none;

  &:focus {
    @apply ring-1 ring-gray-300 dark:ring-gray-600;
  }

  &.textarea {
    @apply resize-none;
  }
}

.input-enabled {
  @apply cursor-text;

  &.textarea {
    @apply resize-y;
  }
}
</style>
